import React, { useState } from 'react';
import { projects } from '../data/mock';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { ExternalLink, Calendar, CheckCircle, Code } from 'lucide-react';

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);

  return (
    <section id="projects" className="py-20 bg-[#111111]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold mb-4">
            Featured <span className="bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent">Projects</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 mx-auto rounded-full"></div>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
            A showcase of my software development projects demonstrating practical application of programming skills and problem-solving abilities
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {projects.map((project) => (
            <Card
              key={project.id}
              className="bg-[#1a1a1b] border-gray-800 hover:border-blue-500/50 transition-all duration-300 overflow-hidden group hover:shadow-xl hover:shadow-blue-500/10"
            >
              {/* Project Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1b] via-transparent to-transparent"></div>
                <Badge
                  className="absolute top-4 right-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white border-none"
                >
                  {project.status}
                </Badge>
              </div>

              <CardHeader>
                <CardTitle className="text-2xl text-white group-hover:text-blue-400 transition-colors">
                  {project.title}
                </CardTitle>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Calendar size={16} />
                  <span>{project.year}</span>
                </div>
              </CardHeader>

              <CardContent>
                <p className="text-gray-300 leading-relaxed mb-4">
                  {project.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {project.technologies.slice(0, 4).map((tech, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                    >
                      {tech}
                    </Badge>
                  ))}
                  {project.technologies.length > 4 && (
                    <Badge
                      variant="outline"
                      className="border-gray-600 text-gray-400"
                    >
                      +{project.technologies.length - 4} more
                    </Badge>
                  )}
                </div>
              </CardContent>

              <CardFooter>
                <Button
                  onClick={() => setSelectedProject(project)}
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white transition-all duration-300"
                >
                  View Details <ExternalLink size={16} className="ml-2" />
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>

      {/* Project Details Dialog */}
      <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
        <DialogContent className="bg-[#1a1a1b] border-gray-800 text-white max-w-3xl max-h-[80vh] overflow-y-auto">
          {selectedProject && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <DialogTitle className="text-3xl mb-2">
                      {selectedProject.title}
                    </DialogTitle>
                    <div className="flex items-center gap-3">
                      <Badge className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white border-none">
                        {selectedProject.status}
                      </Badge>
                      <span className="text-gray-400 flex items-center gap-1">
                        <Calendar size={16} />
                        {selectedProject.year}
                      </span>
                    </div>
                  </div>
                </div>
                <img
                  src={selectedProject.image}
                  alt={selectedProject.title}
                  className="w-full h-64 object-cover rounded-lg mb-6"
                />
              </DialogHeader>

              <DialogDescription className="text-gray-300 space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-3 flex items-center gap-2">
                    <Code size={20} className="text-blue-400" />
                    Project Overview
                  </h3>
                  <p className="leading-relaxed">
                    {selectedProject.longDescription}
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-white mb-3 flex items-center gap-2">
                    <CheckCircle size={20} className="text-cyan-400" />
                    Key Features
                  </h3>
                  <ul className="space-y-2">
                    {selectedProject.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle size={16} className="text-cyan-400 mt-1 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-white mb-3">Technologies Used</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.technologies.map((tech, index) => (
                      <Badge
                        key={index}
                        variant="outline"
                        className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                      >
                        {tech}
                      </Badge>
                    ))}
                  </div>
                </div>
              </DialogDescription>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Projects;
